import { useCallback, useMemo } from 'react';

import { AlertTriangleIcon, EyeIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_DIALOG_PREVIEW } from '@/components/organisms/Modals/ModalConductor';
import { renderDialogIcon } from '@/components/organisms/SubNav/TreeView/Item';
import { Button } from '@/components/ui/button';
import { AutocompleteOption, Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useDialogs from '@/hooks/useDialogs';
import { pushModal } from '@/redux/modals/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { AuthConfig, AuthStep } from '@/shared/types/node';

import { AuthPageStepPanelProps } from '../types';
import useAuthPageValidation from '../useAuthPageValidation';
import AuthPagePanelWrapper from './AuthPagePanelWrapper';

interface NodeOption {
  dialog_name: string;
  dialog_id: string;
  value: string;
  label: string;
  node_type: string;
}

const AuthPageSuccessPanel = ({
  step,
  authConfig,
  onUpdate,
  onClose,
  showNodeSelector = false,
  showErrors = false,
}: AuthPageStepPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brainId = useSelector(selectBrainId);
  const { detailedOptions } = useDialogs(brainId);

  const stepFieldKey = `${step}_step` as const;
  const stepConfig: AuthStep | undefined = authConfig[stepFieldKey];

  const { getFieldError, getThresholdExceedsMax } = useAuthPageValidation(authConfig, showErrors);
  const thresholdError = getFieldError(`${step}-threshold`);
  const thresholdExceedsMax = getThresholdExceedsMax(step);
  const successNodeError = getFieldError('success_node_id');

  const threshold = stepConfig?.success_points_threshold ?? 0;
  const successNodeId = authConfig.success_node_id ?? '';

  const nodeOptions: AutocompleteOption<NodeOption>[] = useMemo(
    () =>
      detailedOptions
        // Unknown nodes are catch-all entry points and never make sense as a
        // post-auth jump target — keep them out of the picker.
        .filter((opt) => opt.node_type !== 'unknown')
        .map((opt) => ({
          value: opt.value,
          label: opt.label,
          category: opt.dialog_name,
          data: opt as NodeOption,
        })),
    [detailedOptions]
  );

  const selectedNode = useMemo(
    () => detailedOptions.find((opt) => opt.value === successNodeId),
    [detailedOptions, successNodeId]
  );

  const maxPoints = useMemo(
    () => stepConfig?.questions?.reduce((sum, q) => sum + (q.points || 0), 0) ?? 0,
    [stepConfig?.questions]
  );

  const updateThreshold = useCallback(
    (value: number) => {
      onUpdate({
        [stepFieldKey]: { ...stepConfig, success_points_threshold: value },
      } as Partial<AuthConfig>);
    },
    [onUpdate, stepConfig, stepFieldKey]
  );

  const handleThresholdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (raw === '') {
        updateThreshold(0);
        return;
      }
      const value = parseInt(raw, 10);
      if (isNaN(value)) {
        return;
      }
      updateThreshold(value);
    },
    [updateThreshold]
  );

  const handleThresholdBlur = useCallback(() => {
    const minThreshold = maxPoints > 0 ? 1 : 0;
    const clamped = Math.max(minThreshold, Math.min(threshold, maxPoints));
    if (clamped !== threshold) {
      updateThreshold(clamped);
    }
  }, [threshold, maxPoints, updateThreshold]);

  const handleNodeChange = useCallback(
    (value: string) => {
      onUpdate({ success_node_id: value || undefined });
    },
    [onUpdate]
  );

  const handlePreviewDialog = useCallback(() => {
    if (!selectedNode) {
      return;
    }
    dispatch(
      pushModal(MODAL_DIALOG_PREVIEW, {
        name: selectedNode.dialog_name,
        dialogId: selectedNode.dialog_id,
      })
    );
  }, [dispatch, selectedNode]);

  return (
    <AuthPagePanelWrapper
      title={t(
        step === 'identification'
          ? 'dialog.auth.success_label_identification'
          : 'dialog.auth.success_label_verification'
      )}
      onClose={onClose}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <Label className="text-sm font-semibold">
              {t('dialog.auth.success_threshold_label')}
            </Label>
            <InfoTooltip content={t('dialog.auth.success_threshold_tooltip')}>
              <InfoIcon size={14} className="text-muted-foreground" />
            </InfoTooltip>
          </div>
          <Input
            type="number"
            min={1}
            max={maxPoints || undefined}
            value={threshold || ''}
            onChange={handleThresholdChange}
            onBlur={handleThresholdBlur}
            aria-invalid={!!thresholdError || thresholdExceedsMax}
            className="aria-invalid:border-warning"
          />
          {thresholdError && <p className="text-xs text-warning">{thresholdError}</p>}
          {thresholdExceedsMax && !thresholdError && (
            <p className="flex items-center gap-1 text-xs text-warning">
              <AlertTriangleIcon size={12} />
              {t('dialog.auth.success_threshold_warning')}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {t('dialog.auth.success_condition_description')}
          </p>
        </div>

        {/* When identification succeeds and verification is next */}
        {!showNodeSelector && step === 'identification' && (
          <p className="text-xs text-muted-foreground">
            {t('dialog.auth.success_proceeds_to_verification')}
          </p>
        )}

        {/* Trigger node on success */}
        {showNodeSelector && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t('dialog.auth.success_node_label')}</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Autocomplete<NodeOption>
                  options={nodeOptions}
                  value={successNodeId}
                  onChange={handleNodeChange}
                  placeholder={t('dialog.jump_to.placeholder')}
                  className="py-1.5"
                  renderOption={(option) => (
                    <span className="flex items-center gap-2">
                      {option.data?.node_type && renderDialogIcon(option.data.node_type)}
                      <span className="truncate">{option.label}</span>
                    </span>
                  )}
                  renderValue={(option) => (
                    <span className="flex items-center gap-2">
                      {option.data?.node_type && renderDialogIcon(option.data.node_type)}
                      <span className="truncate">{option.label}</span>
                    </span>
                  )}
                />
              </div>
              {selectedNode?.dialog_id && (
                <InfoTooltip content={t('ai_agents.reminder.preview_tooltip')} side="top">
                  <Button variant="outline-neutral" size="icon-md" onClick={handlePreviewDialog}>
                    <EyeIcon size={16} />
                  </Button>
                </InfoTooltip>
              )}
            </div>
            {successNodeError && <p className="text-xs text-warning">{successNodeError}</p>}
            <p className="text-xs text-muted-foreground">
              {t('dialog.auth.success_node_description')}
            </p>
          </div>
        )}
      </div>
    </AuthPagePanelWrapper>
  );
};

export default AuthPageSuccessPanel;
